import { useEffect, useRef, useState } from 'react'
import { apiErrorMessage, apiFetch } from '@/lib/csrf'
import {
  decisionsFromPreview,
  previewBody,
  undecidedRoomIds,
  type MappingDecisions,
  type MappingRequest,
  type RoomMappingPreview,
} from '@/lib/combined-inventory'

/**
 * The mapping step's whole state for one dialog: the server's step, the decisions the operator holds
 * (seeded from the server's first answer), and whether the step may be saved. Save is held only while
 * some room has no choice ("Optimistic room mapping"): the decisions are what is saved and the server
 * re-checks them, so a redraw in flight (it only refreshes the room count line) never holds it.
 */
export function useRoomMapping(settingsPath: string, request: MappingRequest | null) {
  const [decisions, setDecisions] = useState<MappingDecisions | null>(null)
  const requestKey = request ? JSON.stringify(request) : null

  useEffect(() => {
    setDecisions(null)
  }, [requestKey])

  const { preview, loading, error } = useRoomMappingPreview(settingsPath, request, decisions)

  useEffect(() => {
    if (preview && decisions === null) setDecisions(decisionsFromPreview(preview))
  }, [preview, decisions])

  const undecided = preview && decisions ? undecidedRoomIds(preview, decisions) : []
  const canSave = !!preview && !!decisions && !error && undecided.length === 0

  return { preview, decisions, setDecisions, loading, error, undecided, canSave }
}

/** How long the step waits after the last change before asking the server again. */
export const MAPPING_PREVIEW_DEBOUNCE_MS = 250

/**
 * THE ROOM MAPPING STEP, AS THE SERVER DRAWS IT.
 *
 * Posts the request and the decisions held so far to `<settingsPath>/groups/mapping-preview` and
 * returns the server's step: rows, option labels and the combined room count line. The first
 * ask goes out at once (decisions still null, so the server pre-fills them); every later one is
 * debounced, and an answer that arrives after a newer ask is dropped, so the step never shows the
 * count for decisions the operator has already changed.
 *
 * `request: null` (a closed dialog) asks nothing and clears the step.
 */
export function useRoomMappingPreview(
  settingsPath: string,
  request: MappingRequest | null,
  decisions: MappingDecisions | null,
): { preview: RoomMappingPreview | null; loading: boolean; error: string | null } {
  const [preview, setPreview] = useState<RoomMappingPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const latest = useRef(0)

  const requestKey = request ? JSON.stringify(request) : null
  const key = requestKey ? JSON.stringify(previewBody(request!, decisions)) : null

  // A different question (another lead, another listing) starts from a blank step.
  useEffect(() => {
    setPreview(null)
    setError(null)
  }, [requestKey])

  useEffect(() => {
    if (!key) {
      latest.current += 1
      setLoading(false)
      return
    }

    const id = ++latest.current
    setLoading(true)
    const timer = window.setTimeout(
      async () => {
        try {
          const response = await apiFetch(`${settingsPath}/groups/mapping-preview`, { method: 'POST', body: key })
          if (!response.ok) throw new Error(`mapping preview ${response.status}`)
          const body = (await response.json()) as RoomMappingPreview
          if (id !== latest.current) return
          setPreview(body)
          setError(null)
        } catch (e) {
          if (id !== latest.current) return
          setError(apiErrorMessage(e, 'The room mapping could not be loaded. Close this and try again.'))
        } finally {
          if (id === latest.current) setLoading(false)
        }
      },
      decisions === null ? 0 : MAPPING_PREVIEW_DEBOUNCE_MS,
    )

    return () => window.clearTimeout(timer)
    // `key` carries the request and the decisions; the hook re-asks exactly when either changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, settingsPath])

  return { preview, loading, error }
}
